import { useMemo, useState, useEffect } from "react";
import { Download, Image as ImageIcon, ExternalLink, Trash2, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { StoryCard, PostCard } from "./ShareCardPreview";
import {
  SC_DEFAULT_CONFIG,
  SC_TEMPLATES,
  loadTemplates,
  saveTemplate,
  deleteTemplate,
} from "../lib/shareCardConfig";

const FALLBACK = {
  campaign: {
    title: "SCOOTIVE ON TOUR — SIERPIEŃ 2026",
    description: "Wybierzcie miejsce, do którego pojedzie team Scootive w wakacje!",
    results_title: "Top miasta",
    ends_at: "2026-08-31T22:59:00.000Z",
  },
  total: 12_847,
  winner: { city: "Gdańsk", skatepark: "Skatepark Brzeźno", region: "Pomorskie", votes: 3214, pct: 25.0 },
  top: [
    { city: "Warszawa", skatepark: "Skatepark Mokotów", region: "Mazowieckie", votes: 2871, pct: 22.3 },
    { city: "Kraków", skatepark: "Skatepark Mistrzejowice", region: "Małopolskie", votes: 1936, pct: 15.1 },
    { city: "Wrocław", skatepark: "Skatepark Karłowice", region: "Dolnośląskie", votes: 1442, pct: 11.2 },
    { city: "Poznań", skatepark: "Skatepark Cegielnia", region: "Wielkopolskie", votes: 987, pct: 7.7 },
  ],
};

function toData(campaign, leaderboard, totalVotes) {
  if (!campaign || !leaderboard?.length) return FALLBACK;
  const w = leaderboard[0];
  return {
    campaign: { ...campaign, ends_at: campaign.ends_at },
    total: totalVotes || 0,
    winner: {
      city: w.city,
      skatepark: w.skatepark || "",
      region: w.region || "",
      votes: w.votes,
      pct: w.percentage,
    },
    top: leaderboard.slice(1, 5).map((r) => ({
      city: r.city,
      skatepark: r.skatepark || "",
      region: r.region || "",
      votes: r.votes,
      pct: r.percentage,
    })),
  };
}

export default function AdminShareCardPanel({ campaign, leaderboard, totalVotes }) {
  const [config, setConfig] = useState(SC_DEFAULT_CONFIG);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("classic-green");
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    setCustomTemplates(loadTemplates());
    if (campaign?.id) {
      const raw = localStorage.getItem(`scootive_sharecard_${campaign.id}`);
      if (raw) {
        try {
          setConfig({ ...SC_DEFAULT_CONFIG, ...JSON.parse(raw) });
        } catch {
          /* ignore */
        }
      }
    }
  }, [campaign?.id]);

  const data = useMemo(
    () => toData(campaign, leaderboard, totalVotes),
    [campaign, leaderboard, totalVotes]
  );

  const all = [...SC_TEMPLATES, ...customTemplates];

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = all.find((t) => t.id === id);
    if (tpl) setConfig({ ...SC_DEFAULT_CONFIG, ...tpl.config });
  };

  const saveAsTemplate = () => {
    const name = window.prompt("Nazwa szablonu:");
    if (!name) return;
    const next = saveTemplate(name, config);
    setCustomTemplates(next.filter((t) => t.custom));
    toast.success(`Szablon „${name}" zapisany`);
  };

  const removeTemplate = (id) => {
    if (!window.confirm("Usunąć szablon?")) return;
    const next = deleteTemplate(id);
    setCustomTemplates(next.filter((t) => t.custom));
    toast.success("Usunięto");
  };

  const saveForCampaign = async () => {
    if (!campaign?.id) return;
    localStorage.setItem(`scootive_sharecard_${campaign.id}`, JSON.stringify(config));
    try {
      const { api } = await import("../lib/api");
      await api.patch(`/campaigns/${campaign.id}`, { share_card_config: config });
      toast.success("Zapisano dla kampanii (lokalnie + backend)");
    } catch (e) {
      toast.success("Zapisano lokalnie (backend nieosiągalny)");
    }
  };

  const downloadPng = async (format) => {
    if (!campaign?.id) {
      toast.error("Wybierz najpierw aktywną kampanię");
      return;
    }
    try {
      const { api } = await import("../lib/api");
      await api.patch(`/campaigns/${campaign.id}`, { share_card_config: config });
    } catch {
      /* ignore */
    }
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${apiBase}/api/campaigns/${campaign.id}/share-card?format=${format}&download=1&t=${Date.now()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const Field = ({ label, children, hint }) => (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-black/55 mb-2">
        {label}
      </p>
      {children}
      {hint && <p className="text-[10px] text-black/45 mt-1.5">{hint}</p>}
    </div>
  );

  const SizeSeg = ({ value, onChange, tid }) => (
    <div className="flex gap-1">
      {["S", "M", "L", "XL"].map((s) => (
        <button
          key={s}
          type="button"
          data-testid={`${tid}-${s}`}
          onClick={() => onChange(s)}
          className={`flex-1 h-9 rounded-lg text-xs font-medium transition-all ${
            value === s
              ? "bg-brand-green text-white"
              : "bg-black/5 text-black/65 hover:bg-black/10"
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {/* CONFIG */}
      <div className="surface-soft p-5 sm:p-6 space-y-5 max-h-[85vh] overflow-y-auto">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight mb-1 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 brand-green" /> Konfiguracja Share Card
          </h3>
          <p className="text-xs text-black/55">
            Personalizuj IG Story (1080×1920) i Post (1080×1080). Wszystkie zmiany na żywo po
            prawej.
          </p>
        </div>

        <Field label="Szablon">
          <div className="flex items-center gap-2">
            <Select value={templateId} onValueChange={applyTemplate}>
              <SelectTrigger
                data-testid="sc-template-select"
                className="h-10 bg-white border-black/10 input-rounded flex-1"
              >
                <SelectValue placeholder="Wybierz szablon" />
              </SelectTrigger>
              <SelectContent>
                {SC_TEMPLATES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
                {customTemplates.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-black/40">
                      Twoje
                    </div>
                    {customTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            <Button
              onClick={saveAsTemplate}
              data-testid="sc-save-tpl-btn"
              className="btn-soft h-10 px-3 gap-1.5 text-xs"
            >
              <BookmarkPlus className="w-3.5 h-3.5" /> Zapisz jako szablon
            </Button>
          </div>
          {customTemplates.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {customTemplates.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/5 text-[10px]"
                >
                  {t.name}
                  <button
                    onClick={() => removeTemplate(t.id)}
                    className="text-black/40 hover:text-red-600"
                    title="Usuń"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>

        <Field label="Kolor akcent">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={config.accent}
              onChange={(e) => setConfig({ ...config, accent: e.target.value })}
              className="h-10 w-12 rounded-lg border border-black/10 cursor-pointer"
              data-testid="sc-accent"
            />
            <Input
              value={config.accent}
              onChange={(e) => setConfig({ ...config, accent: e.target.value })}
              className="bg-white border-black/10 input-rounded h-10 text-xs font-mono-disp"
            />
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Domena na karcie">
            <Input
              value={config.domain}
              onChange={(e) => setConfig({ ...config, domain: e.target.value })}
              className="bg-white border-black/10 input-rounded h-10 text-sm"
              data-testid="sc-domain"
            />
          </Field>
          <Field label="Hashtag">
            <Input
              value={config.hashtag}
              onChange={(e) =>
                setConfig({ ...config, hashtag: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })
              }
              className="bg-white border-black/10 input-rounded h-10 text-sm"
              data-testid="sc-hashtag"
            />
          </Field>
        </div>

        <Field label="Niestandardowy tytuł rankingu" hint="Domyślnie używamy tytułu rankingu z kampanii.">
          <Input
            value={config.topLabel === "auto" ? "" : config.topLabel}
            onChange={(e) =>
              setConfig({ ...config, topLabel: e.target.value || "auto" })
            }
            placeholder='np. „Top 5 skateparków" (puste = auto)'
            className="bg-white border-black/10 input-rounded h-10 text-sm"
            data-testid="sc-top-label"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Rozmiar nazwy zwycięzcy">
            <SizeSeg
              value={config.winnerSize}
              onChange={(s) => setConfig({ ...config, winnerSize: s })}
              tid="sc-size-winner"
            />
          </Field>
          <Field label="Rozmiar tytułu kampanii">
            <SizeSeg
              value={config.campaignTitleSize}
              onChange={(s) => setConfig({ ...config, campaignTitleSize: s })}
              tid="sc-size-campaign"
            />
          </Field>
        </div>

        <Field label="Elementy karty">
          <div className="grid grid-cols-1 gap-2">
            {[
              ["showCampaignTitle", "Tytuł kampanii"],
              ["showCampaignDescription", "Opis kampanii"],
              ["showWinnerDescription", "Opis miejsca (np. skatepark)"],
              ["showWinnerRegion", "Region/województwo"],
              ["showStats", "Blok statystyk (liczby głosów)"],
              ["showTop", "Top 5 z paskami"],
              ["showHashtag", "Hashtag w stopce"],
              ["showEndsAt", "Data zakończenia głosowania"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between p-2.5 rounded-lg bg-black/[0.03]">
                <span className="text-sm">{label}</span>
                <Switch
                  checked={config[key]}
                  onCheckedChange={(v) => setConfig({ ...config, [key]: v })}
                  data-testid={`sc-${key}`}
                />
              </label>
            ))}
          </div>
        </Field>

        <div className="flex items-center gap-2 pt-2 border-t border-black/5 flex-wrap">
          <Button
            onClick={saveForCampaign}
            disabled={!campaign?.id}
            data-testid="sc-save-btn"
            className="btn-brand h-10 px-4 gap-2 text-xs font-semibold"
          >
            <Download className="w-3.5 h-3.5" /> Zapisz dla kampanii
          </Button>
          <Button
            onClick={() => downloadPng("story")}
            disabled={!campaign?.id}
            data-testid="sc-png-story-btn"
            className="btn-soft h-10 px-3 gap-1.5 text-xs font-semibold"
          >
            <Download className="w-3.5 h-3.5" /> PNG · Story
          </Button>
          <Button
            onClick={() => downloadPng("post")}
            disabled={!campaign?.id}
            data-testid="sc-png-post-btn"
            className="btn-soft h-10 px-3 gap-1.5 text-xs font-semibold"
          >
            <Download className="w-3.5 h-3.5" /> PNG · Post
          </Button>
          <a
            href="/preview/share-card"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-black/55 hover:text-brand-green inline-flex items-center gap-1.5"
          >
            <ExternalLink className="w-3 h-3" /> Pełen podgląd
          </a>
        </div>

        <p className="text-[11px] leading-relaxed text-black/55 pt-2">
          <strong className="text-brand-green">Renderer PNG online.</strong> Endpoint{" "}
          <code className="bg-black/5 px-1 py-0.5 rounded">
            GET /api/campaigns/:id/share-card?format=story|post
          </code>{" "}
          generuje gotowy obraz 1080×1920 / 1080×1080 (Puppeteer + headless Chrome). Konfiguracja zapisuje się też w bazie kampanii.
        </p>
      </div>

      {/* PREVIEW */}
      <div className="space-y-5 max-h-[85vh] overflow-y-auto">
        <div className="surface-soft p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/55">
              IG Story · TikTok
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setScale(scale === 0.5 ? 0.35 : 0.5)}
                className="text-[10px] uppercase tracking-wider px-2 py-1 bg-black/5 rounded-full hover:bg-black/10"
              >
                {scale === 0.5 ? "Pomniejsz" : "Powiększ"}
              </button>
              <p className="text-[10px] tabular-nums bg-black/5 px-2 py-1 rounded-full text-black/55">
                1080 × 1920
              </p>
            </div>
          </div>
          <div className="flex justify-center bg-[#f3f4f3] rounded-xl p-3 overflow-hidden">
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top center", height: 960 * scale }}>
              <StoryCard data={data} config={config} />
            </div>
          </div>
        </div>

        <div className="surface-soft p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/55">
              IG Post · FB Post
            </p>
            <p className="text-[10px] tabular-nums bg-black/5 px-2 py-1 rounded-full text-black/55">
              1080 × 1080
            </p>
          </div>
          <div className="flex justify-center bg-[#f3f4f3] rounded-xl p-3 overflow-hidden">
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top center", height: 540 * scale }}>
              <PostCard data={data} config={config} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
